import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { NotifBell } from "@/pages/Notifications";

const MP_ROUTE = {
  integrator: "/app/marketplace/integrator",
  workforce: "/app/marketplace/workforce",
  event_tech: "/app/marketplace/event-tech",
};

export function Layout({ children }) {
  const { user, signout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.roles?.includes("super_admin");
  const [marketplaces, setMarketplaces] = useState([]);

  // CP-15.3 — populate the marketplace nav from access_policy. We re-query
  // when the signed-in user changes so role flips reflect immediately.
  useEffect(() => {
    if (!user) {
      setMarketplaces([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api("/marketplaces");
        if (!cancelled) {
          setMarketplaces((r.items || []).filter((m) => m.can_view));
        }
      } catch {
        // Non-fatal — nav simply omits the marketplaces section.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const linkCls = (path) =>
    `px-3 py-1.5 rounded-full text-sm font-medium transition ${
      loc.pathname === path
        ? "bg-[var(--avcp-primary-soft)] text-[var(--avcp-primary)]"
        : "text-[var(--avcp-ink-soft)] hover:text-[var(--avcp-ink)]"
    }`;

  return (
    <div className="min-h-screen">
      <header className="border-b border-[var(--avcp-line)] bg-[var(--avcp-surface)]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to={user ? "/app" : "/"} data-testid={TID.navHome} className="flex items-center gap-2">
            <span
              aria-hidden
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, var(--avcp-primary) 0%, #155e75 100%)",
              }}
            >
              <span className="text-white font-bold text-sm tracking-tight">WP</span>
            </span>
            <span className="font-semibold tracking-tight">
              Workforce Partners
            </span>
          </Link>
          {user ? (
            <nav className="flex items-center gap-1">
              <Link to="/app" className={linkCls("/app")} data-testid={TID.navHome}>Home</Link>
              {/* CP-15.3 — marketplaces are access_policy-driven; one link per MP the viewer can see */}
              {marketplaces.map((m) => (
                <Link
                  key={m.code}
                  to={MP_ROUTE[m.code] || "/app"}
                  className={linkCls(MP_ROUTE[m.code])}
                  data-testid={`nav-marketplace-${m.code}`}
                  title={m.description || m.display_name}
                >
                  {m.nav_label || m.display_name}
                </Link>
              ))}
              {(user.roles?.includes("contractor") || user.roles?.includes("lead_contractor") || user.roles?.includes("helper")) && (
                <>
                  <Link to="/app/contractor-profile" className={linkCls("/app/contractor-profile")} data-testid={TID.navProfile}>Profile</Link>
                  <Link to="/app/contractor-profile/availability" className={linkCls("/app/contractor-profile/availability")} data-testid="nav-availability">Availability</Link>
                </>
              )}
              {(user.roles?.includes("client") || user.roles?.includes("super_admin")) && (
                <>
                  <Link to="/app/jobs" className={linkCls("/app/jobs")} data-testid="nav-jobs">Projects</Link>
                  <Link to="/app/marketplace/leads" className={linkCls("/app/marketplace/leads")} data-testid="nav-marketplace-leads">Lead Technicians</Link>
                  <Link to="/app/marketplace/crew-shape" className={linkCls("/app/marketplace/crew-shape")} data-testid="nav-crew-shape-search">Crew Shape Search</Link>
                  {/* CP-15.5 — legacy Phase-12 marketplace search link removed from the visible
                      navigation now that Workstream-B marketplaces are the canonical surface.
                      The /app/marketplace/search route remains mounted so direct URLs keep
                      working and external bookmarks are not broken. */}
                </>
              )}
              {(user.roles?.includes("lead_contractor") || user.roles?.includes("super_admin")) && (
                <>
                  <Link to="/app/leads/me/bookings" className={linkCls("/app/leads/me/bookings")} data-testid="nav-lead-inbox">Booking Requests</Link>
                  <Link to="/app/marketplace/helpers" className={linkCls("/app/marketplace/helpers")} data-testid="nav-marketplace-helpers">Helper Technicians</Link>
                </>
              )}
              {(user.roles?.includes("helper") || user.roles?.includes("lead_contractor") || user.roles?.includes("super_admin")) && (
                <Link to="/app/helpers/me/invitations" className={linkCls("/app/helpers/me/invitations")} data-testid="nav-helper-invitations">Invitations</Link>
              )}
              <Link to="/app/account" className={linkCls("/app/account")} data-testid={TID.navAccount}>Account</Link>
              <Link to="/app/rfq" className={linkCls("/app/rfq")} data-testid={TID.navRfq}>RFQs</Link>
              <Link to="/app/security" className={linkCls("/app/security")} data-testid={TID.navSecurity}>Security</Link>
              {isAdmin && (
                <Link to="/app/admin" className={linkCls("/app/admin")} data-testid={TID.navAdmin}>Admin</Link>
              )}
              <NotifBell />
              <button
                data-testid={TID.navLogout}
                onClick={async () => { await signout(); nav("/signin"); }}
                className="avcp-btn ghost ml-2"
              >
                Sign out
              </button>
            </nav>
          ) : (
            <nav className="flex items-center gap-2">
              <Link to="/signin" className="avcp-btn ghost">Sign in</Link>
              <Link to="/signup" className="avcp-btn primary">Get started</Link>
            </nav>
          )}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">{children}</main>
      <footer className="max-w-6xl mx-auto px-6 py-8 text-xs text-[var(--avcp-ink-soft)]">
        Workforce Partners · The Professional AV Workforce Network.
      </footer>
    </div>
  );
}
